import { logNetworkRequest } from 'Diagnostics/diagnostics';
import { CustomErrorType } from 'Models/index';
import { normalizePageSize } from 'Models/pagination';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { SearchRequestDefaults } from 'Models/uiConfig/requestTypes';
import { convertibleFromObject } from 'Modules/converter/index';
import { correctionLevelToParam } from 'Modules/serverApi/common/flags';

import { facetsToConvertible, sortToConvertible } from './common/convert';
import { asCmError, GET, GETBACKUP, getErrorSearchResponse } from './common/httpClient';
import ResponseValidationError from './common/responseValidationError';
import { getSearchResponseValidationError, isSearchResponseShape } from './common/validators';
import type { SearchRequest, ServerModel } from './types';

export { isSearchResponseShape as isSearchResponse } from './common/validators';

const { requestDefaults, responseHandler } = requestConfig;
const endpoint = 'search.json';

function getStringParam(value: string | undefined, defaultValue?: string): string | undefined {
  return value ?? defaultValue;
}

function getNumberParam(value: number | undefined, defaultValue?: number): number | undefined {
  return value ?? defaultValue;
}

function getPageSizeParam(request: SearchRequest, defaults: SearchRequestDefaults): number | undefined {
  return (
    request.pageSize ??
    (defaults.pageSize === undefined ? undefined : normalizePageSize(defaults.pageSize).pageSize)
  );
}

export default async function search(
  request: SearchRequest,
  abortSignal?: AbortSignal,
  onInit?: () => void,
): Promise<ServerModel.SearchResponse> {
  const extra = {
    ...(requestDefaults.extra ?? {}),
    ...(request.extra ?? {}),
  };

  const params = {
    query: encodeURIComponent(request.query ?? requestDefaults.query ?? ''),
    hiddenquery: encodeURIComponent(request.hiddenQuery ?? requestDefaults.hiddenQuery ?? ''),
    filterquery: encodeURIComponent(request.filterQuery ?? requestDefaults.filterQuery ?? ''),
    querycorrectionlevel: correctionLevelToParam(
      request.queryCorrectionLevel,
      requestDefaults.queryCorrectionLevel,
    ),
    page: getNumberParam(request.pageNumber, requestDefaults.pageNumber),
    pagesize: getPageSizeParam(request, requestDefaults),
    mode: encodeURIComponent(request.mode ?? requestDefaults.mode ?? ''),
    sort: sortToConvertible(request.sort ?? requestDefaults.sort ?? null),
    catalog: encodeURIComponent(request.catalog ?? requestDefaults.catalog ?? ''),
    facet: facetsToConvertible(request, requestDefaults),
    extra: await convertibleFromObject(extra),
    variantsMode: encodeURIComponent(request.variantsMode ?? requestDefaults.variantsMode ?? ''),
    referrer: getStringParam(request.referrer, requestDefaults.referrer),
    ...(request.explainScores ? { explainscores: 'true' } : {}),
  };

  try {
    return getSearchResponse(await GET(endpoint, params, { signal: abortSignal }), onInit);
  } catch (primaryErr) {
    const pe = asCmError(primaryErr);
    if (pe.type !== CustomErrorType.requestAborted) {
      console.warn(`The search request failed: ${pe.message}`);
    }

    try {
      const backupResponse = getSearchResponse(
        await GETBACKUP(endpoint, params, { signal: abortSignal }),
        onInit,
      );
      // backup succeeded — log degraded state (skip aborted requests)
      if (pe.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({ endpoint, scenario: 'backup_used', primaryError: pe });
      }
      return backupResponse;
    } catch (backupErr) {
      const be = asCmError(backupErr);
      // both failed — log critical failure (skip aborted requests)
      if (pe.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({ endpoint, scenario: 'backup_failed', primaryError: pe, backupError: be });
      }
      const message = await getErrorSearchResponse(backupErr);
      return message;
    }
  }
}

function getSearchResponse(
  value: unknown,
  onInit?: () => void,
): Promise<ServerModel.SearchResponse> | ServerModel.SearchResponse {
  if (!isSearchResponseShape(value)) {
    const validationError = getSearchResponseValidationError(value) ?? 'unknown validation error';
    throw new ResponseValidationError(`search.json response validation failed: ${validationError}`);
  }

  return responseHandler(value, onInit);
}
